import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { EditTaskItemDto, FetchError, queryKeys, TaskItemDetailDto, useAccessToken } from "src/http";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  taskItemGuid: string;
  description: string;
};

const useEditTaskItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editTaskItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditTaskItemDto = {
      description: variables.description,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/${variables.taskItemGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editTaskItemRequest };
};

const useEditTaskItem = () => {
  const { editTaskItemRequest } = useEditTaskItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editTaskItemRequest,
    onMutate: async (variables) => {
      let cachedDetailData: TaskItemDetailDto | undefined;

      cachedDetailData = await optimisticallyUpdateQueryData<TaskItemDetailDto>(
        queryClient,
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        () => ({
          description: variables.description,
        })
      );

      return { cachedDetailData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchTaskItem(variables.taskItemGuid);
      signalR.sendRefetchComments(variables.taskItemGuid);
      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid),
        context?.cachedDetailData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemDetail(variables.taskItemGuid));
      queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemFeed(variables.taskItemGuid));

      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);
    },
  });
};

export { useEditTaskItem };
